import React from 'react';
import type { DisposicionDeCarga } from './disposicionDeCarga';
import { SparklesLoader, SparklesLoaderInline } from './SparklesLoader';

/**
 * El NÚCLEO de la pantalla de carga —sparkles + título/subtítulo + barra—, como
 * componente presentacional puro (ADR-2026-09-06-nucleo-de-carga-compartido).
 *
 * Se extrajo de `LoadingAnimation` para que el recuadro "Generando…" de la tanda
 * (`PanelTandaCreaciones`) muestre EXACTAMENTE lo mismo que la pantalla grande, en
 * chico: mismo azul, misma foto de fondo, mismas estrellas, misma barra blanca con
 * relleno `#1644EB`. Antes el recuadro tenía su propio lenguaje (`AnimatedBlurBg`,
 * un ícono de lucide y una barra que barría en loop) y no se parecía en nada a la
 * espera full-screen que el usuario ya conocía.
 *
 * No llama hooks ni calcula nada: recibe el progreso como número. El motor lo pone
 * cada caller —`useGenerationProgress` (RAF) en la pantalla grande, un `setInterval`
 * de 250 ms sobre `progresoPaceado` en el recuadro—. Tampoco inyecta `<style>`: los
 * keyframes `atlas-indet` viven en `index.css`, así N recuadros no agregan N bloques
 * de CSS al DOM.
 *
 * - `variante="grande"`: el markup y las clases son LITERALMENTE los que tenía el
 *   bloque central de `LoadingAnimation`; ese componente lo renderiza sin cambiar
 *   ninguna de sus props públicas (22 call-sites intactos).
 * - `variante="compacto"`: para un recuadro de ~200 px. `SparklesLoaderInline` y no
 *   `SparklesLoader`: este último tiene `size-40` hardcodeado y en el CSS compilado
 *   gana sobre cualquier `size-N` menor que se le pase por `className`. Sin
 *   `role="status"`: N recuadros anunciando "Generando…" a la vez es ruido para un
 *   lector de pantalla, y la pantalla grande ya lo anuncia cuando corresponde.
 */
export interface NucleoCargaProps {
  /** `grande` = el bloque central de `LoadingAnimation`; `compacto` = el recuadro de la tanda. */
  variante?: 'grande' | 'compacto';
  /**
   * Sólo `compacto`: cómo se acomoda según lo que mide el recuadro
   * (`disposicionDeCarga`, ADR-2026-10-05-el-recuadro-de-carga-se-acomoda-a-su-tamano).
   * `columna` apila (la de siempre); `fila` pone la animación al costado del texto y la
   * barra, para recuadros bajos; `minima` deja sólo la animación y la barra.
   */
  disposicion?: DisposicionDeCarga;
  /** Título ("Generando imágenes…"). */
  titulo: string;
  /** Línea de apoyo debajo del título. */
  subtitulo?: string;
  /** Nota chica DEBAJO de la barra ("Puede tardar varios minutos"): la expectativa de
   *  espera va pegada a la barra que la mide, no al título
   *  (ADR-2026-09-19-las-cards-de-video-avisan-que-tardan). */
  debajoDeLaBarra?: string;
  /** 0-100. Lo calcula el caller; acá sólo se dibuja. */
  progreso: number;
  /** Sin duración conocida: un segmento que barre en loop (`atlas-indet`). */
  indeterminado?: boolean;
  /**
   * Etiqueta del título. La pantalla grande usa `h1` en el onboarding (es el título
   * de la pantalla) y `h2` en la app; el recuadro compacto usa `p` (no es una sección).
   * Default: `h2` en grande, `p` en compacto.
   */
  etiquetaTitulo?: 'h1' | 'h2' | 'p';
  /** Slot entre los textos y la barra: el countdown de video de `LoadingAnimation`. */
  encimaDeLaBarra?: React.ReactNode;
  /** Demora antes de que arranquen las estrellas (sólo `grande`; ver `SparklesLoader`). */
  sparklesDelayMs?: number;
  className?: string;
}

/**
 * La foto de fondo de la pantalla de carga, absoluta sobre su contenedor
 * (`relative overflow-hidden`). Separada del núcleo porque va DETRÁS del contenido
 * y cada contenedor la posiciona por su cuenta.
 */
export function FondoCarga(): JSX.Element {
  return (
    <img
      src="/images/onboarding/generation/loading-bg.jpg"
      alt=""
      aria-hidden
      className="pointer-events-none absolute inset-0 size-full object-cover"
    />
  );
}

/** La barra del compacto (1 px de alto), en las tres disposiciones. */
function BarraCompacta({
  progreso,
  indeterminado,
  className,
}: {
  progreso: number;
  indeterminado: boolean;
  className: string;
}): JSX.Element {
  return (
    <div className={`h-1 overflow-hidden rounded-full bg-white ${className}`}>
      {indeterminado ? (
        <div
          className="atlas-progress-indet h-full w-2/5 rounded-full bg-[#1644EB]"
          style={{ animation: 'atlas-indet 1.3s ease-in-out infinite' }}
        />
      ) : (
        // Acá SÍ hay `transition`: el ancho lo escribe un `setInterval` de
        // 250 ms (no un RAF por frame), y sin transición la barra avanzaría a
        // saltitos de un cuarto de segundo.
        <div
          className="h-full rounded-full bg-[#1644EB] transition-[width] duration-300"
          style={{ width: `${progreso}%` }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progreso)}
          aria-label="Progreso de generación"
        />
      )}
    </div>
  );
}

export function NucleoCarga({
  variante = 'grande',
  disposicion = 'columna',
  titulo,
  subtitulo,
  debajoDeLaBarra,
  progreso,
  indeterminado = false,
  etiquetaTitulo,
  encimaDeLaBarra,
  sparklesDelayMs = 0,
  className,
}: NucleoCargaProps): JSX.Element {
  // Clases escritas LITERALES por variante (nada de interpolar en el nombre de la
  // clase): Tailwind escanea el código fuente como texto y una clase armada en
  // runtime no se compila.
  if (variante === 'compacto') {
    const Titulo = etiquetaTitulo ?? 'p';
    // MÍNIMA: no hay lugar para texto. La animación y la barra; el título queda para el
    // lector de pantalla.
    if (disposicion === 'minima') {
      return (
        <div
          className={
            className
              ? `flex w-full flex-col items-center gap-2 ${className}`
              : 'flex w-full flex-col items-center gap-2'
          }
        >
          <SparklesLoaderInline className="size-8 text-white" />
          <span className="sr-only">{titulo}</span>
          {encimaDeLaBarra}
          <BarraCompacta progreso={progreso} indeterminado={indeterminado} className="w-3/4" />
        </div>
      );
    }
    // FILA: recuadro bajo (un 16:9 en el lienzo o en una grilla). La animación al
    // costado; título, barra y nota a la derecha, alineados a la izquierda.
    if (disposicion === 'fila') {
      return (
        <div
          className={
            className
              ? `flex w-full items-center gap-3 ${className}`
              : 'flex w-full items-center gap-3'
          }
        >
          <SparklesLoaderInline className="size-10 shrink-0 text-white" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <Titulo className="truncate text-[13px] font-[600] leading-[18px] text-[#FAFAFA]">
                {titulo}
              </Titulo>
              {subtitulo && (
                <p className="truncate text-[11px] font-[500] leading-[14px] text-white/75">
                  {subtitulo}
                </p>
              )}
            </div>
            {encimaDeLaBarra}
            <BarraCompacta progreso={progreso} indeterminado={indeterminado} className="w-full max-w-[220px]" />
            {debajoDeLaBarra && (
              <p className="truncate text-[10.5px] font-[500] leading-[13px] text-white/70">
                {debajoDeLaBarra}
              </p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div
        className={
          className
            ? `flex w-full flex-col items-center gap-2 ${className}`
            : 'flex w-full flex-col items-center gap-2'
        }
      >
        <SparklesLoaderInline className="size-12 text-white" />

        <div className="flex w-full flex-col items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <Titulo className="text-center text-[13px] font-[600] leading-[18px] text-[#FAFAFA]">
              {titulo}
            </Titulo>
            {subtitulo && (
              <p className="text-center text-[11px] font-[500] leading-[14px] text-white/75">
                {subtitulo}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col items-center gap-2">
            {encimaDeLaBarra}

            <BarraCompacta progreso={progreso} indeterminado={indeterminado} className="w-2/3" />
            {debajoDeLaBarra && (
              <p className="text-center text-[10.5px] font-[500] leading-[13px] text-white/70">
                {debajoDeLaBarra}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const Titulo = etiquetaTitulo ?? 'h2';
  // Bloque central de `LoadingAnimation`, tal cual estaba: en desktop se acota a 440px.
  return (
    <div
      // Sin `className` el atributo queda LITERALMENTE como estaba en
      // `LoadingAnimation` (ni un espacio de más): mismo DOM, mismas clases.
      className={
        className
          ? `flex min-h-0 flex-1 flex-col items-center justify-center gap-2 lg:mx-auto lg:w-full lg:max-w-[440px] ${className}`
          : 'flex min-h-0 flex-1 flex-col items-center justify-center gap-2 lg:mx-auto lg:w-full lg:max-w-[440px]'
      }
    >
      <SparklesLoader startDelayMs={sparklesDelayMs} />

      <div className="flex w-full flex-col items-center gap-5">
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-2">
          <Titulo className="text-center text-[26px] font-[600] leading-[31px] text-[#FAFAFA]">
            {titulo}
          </Titulo>
          {subtitulo && (
            <p className="text-center text-[16px] font-[500] leading-[21px] text-[#FAFAFA]">
              {subtitulo}
            </p>
          )}
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          {encimaDeLaBarra}

          <div className="h-2 w-full overflow-hidden rounded-full bg-white">
            {indeterminado ? (
              // Duración real desconocida: un segmento que barre en loop.
              <div
                className="atlas-progress-indet h-full w-2/5 rounded-full bg-[#1644EB]"
                style={{ animation: 'atlas-indet 1.3s ease-in-out infinite' }}
              />
            ) : (
              // Sin `transition` de CSS: el ancho ya lo escribe el RAF en cada
              // frame, y una transición encima sólo agregaba ~300ms de lag.
              <div
                className="h-full rounded-full bg-[#1644EB]"
                style={{ width: `${progreso}%` }}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progreso)}
                aria-label="Progreso de generación"
              />
            )}
          </div>
          {debajoDeLaBarra && (
            <p className="text-center text-[13px] font-[500] leading-[17px] text-white/75">
              {debajoDeLaBarra}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default NucleoCarga;
